/**
 * Oceansouth Location-Aware Inventory Display
 * Shows warehouse-specific stock levels on PDP
 * Handles Back in Stock subscriptions via Klaviyo
 */

(function() {
  'use strict';

  const config = window.OceansouthInventoryConfig;
  if (!config) return;

  // Copy comes from locales/*.json via the snippet (oceansouth.bis.*); fallbacks are English.
  const S = config.strings || {};
  // Liquid's `t` filter HTML-escapes its output ("it&#39;s"), and these strings are set
  // with textContent, so decode entities once here.
  const decoder = document.createElement('textarea');
  function decode(html) { decoder.innerHTML = html; return decoder.value; }
  function str(key, fallback, vars) {
    let out = S[key] ? decode(S[key]) : fallback;
    if (vars) Object.keys(vars).forEach((k) => { out = out.split('__' + k.toUpperCase() + '__').join(String(vars[k])); });
    return out;
  }

  // ─── Market Detection ──────────────────────────────────────────────────────

  function getMarketFromUrl(pathname) {
    pathname = pathname || window.location.pathname;
    // EU: any language prefix ending in -eu (en-eu, fr-eu, de-eu etc.)
    if (pathname.match(/\/[a-z]{2}-eu\//)) return 'EU';
    if (pathname.includes('/en-au/')) return 'AU';
    if (pathname.includes('/en-us/')) return 'US';
    if (pathname.includes('/en-nz/')) return 'NZ';
    return null;
  }

  // ─── Inventory Proxy (via Cloudflare Worker) ───────────────────────────────

  async function getInventoryForMarket(variantId, market) {
    if (!config.workerUrl) {
      console.warn('Oceansouth: No worker URL configured');
      return null;
    }

    try {
      const response = await fetch(
        `${config.workerUrl}/inventory?variant_id=${encodeURIComponent(variantId)}&market=${encodeURIComponent(market)}`
      );

      if (!response.ok) {
        console.error('Oceansouth: Inventory proxy error', response.status);
        return null;
      }

      const data = await response.json();
      return data?.available ?? null;

    } catch (e) {
      console.error('Oceansouth: Inventory fetch error', e);
      return null;
    }
  }

  // Prefetched stock for the whole product: { "<variantId>": available }.
  // Populated once per page so variant switches read from memory, not the network.
  // Stays null if the batch endpoint is unavailable (→ per-variant fallback).
  let inventoryByVariant = null;
  // All variants for this product (theme variant-json), parsed once.
  let allVariants = [];

  function loadVariants() {
    const el = document.querySelector('script[data-name="variant-json"]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent) || [];
    } catch (e) {
      console.warn('Oceansouth: could not parse variant-json', e);
      return [];
    }
  }

  // One worker request for every variant's stock (instead of one per variant).
  async function prefetchInventory(variantIds, market) {
    if (!config.workerUrl || !variantIds.length) return null;
    try {
      const ids = variantIds.map(encodeURIComponent).join(',');
      const response = await fetch(
        `${config.workerUrl}/inventory?variant_ids=${ids}&market=${encodeURIComponent(market)}`
      );
      if (!response.ok) return null;
      const data = await response.json();
      // A valid batch response is a plain object map with no `error` key.
      if (!data || typeof data !== 'object' || data.error) return null;
      return data;
    } catch (e) {
      console.error('Oceansouth: batch inventory error', e);
      return null;
    }
  }

  // ─── Stock Display ─────────────────────────────────────────────────────────

  // Shopify's own verdict for a variant, independent of the warehouse figure.
  function isUnavailableInShopify(variantId) {
    const v = allVariants.find(x => String(x.id) === String(variantId));
    return v ? v.available === false : false;
  }

  // ─── Sold-out UI ───────────────────────────────────────────────────────────
  // A variant Shopify itself reports unavailable → the notice takes over the buy box:
  // the quantity picker and the grey "Sold out" button are hidden (CSS on .os-sold-out),
  // a red tag goes beside the price and the panel is the one action on the page.
  // Zero at the local warehouse but still sellable (preorder / continue policy) → the
  // compact behaviour: badge + panel, buy button untouched.
  let soldOutUI = false;      // stand-out state is on
  let subscribed = false;     // success card showing
  let barSuppressed = true;   // panel is in view → phone bar hidden

  function inventoryRoot() {
    const host = document.getElementById('oceansouth-inventory');
    return (host && host.closest('.yv_product_content_section')) || document.body;
  }

  function setSoldOutUI(on) {
    soldOutUI = !!on;
    const root = inventoryRoot();
    root.classList.toggle('os-sold-out', soldOutUI);
    syncPriceTag();
    const other = document.getElementById('os-bis-other');
    if (other) {
      const othersAvailable = allVariants.some((v) => v.available && String(v.id) !== String(currentVariantId));
      other.hidden = !(soldOutUI && othersAvailable);
    }
    updateBar();
  }

  // The theme rebuilds the price box's children on every variant change, so the tag is
  // re-synced from an observer (set up in init) rather than inserted once.
  function priceBox() {
    const root = inventoryRoot();
    return root.querySelector('[data-price-main]') || root.querySelector('.yv-prizebox');
  }

  function syncPriceTag() {
    const box = priceBox();
    if (!box) return;
    const tag = box.querySelector('.os-soldout-tag');
    if (soldOutUI && !tag) {
      const price = box.querySelector('.yv-product-price');
      if (!price) return;
      const el = document.createElement('span');
      el.className = 'os-soldout-tag';
      el.setAttribute('aria-hidden', 'true'); // the theme already has a visually-hidden "Sold out"
      el.textContent = str('soldOut', 'Sold out');
      price.insertAdjacentElement('afterend', el);
    } else if (!soldOutUI && tag) {
      tag.remove();
    }
  }

  function watchPriceBox() {
    if (!('MutationObserver' in window)) return;
    // Watch the whole product-info column: the theme may replace the price box's children
    // or the box itself on a variant change. Debounced to one sync per frame; inserting
    // the tag is itself a mutation, but the next sync finds it present and does nothing.
    let scheduled = false;
    new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      setTimeout(() => { scheduled = false; syncPriceTag(); }, 0); // not rAF: must run in background tabs too
    }).observe(inventoryRoot(), { childList: true, subtree: true });
  }

  function updateBar() {
    const bar = document.getElementById('os-bis-bar');
    if (bar) bar.hidden = !(soldOutUI && !subscribed && !barSuppressed);
  }

  function showBIS(bisForm) {
    bisForm.hidden = false;
    bisForm.setAttribute('aria-hidden', 'false');
  }

  function hideBIS(bisForm) {
    bisForm.hidden = true;
    bisForm.setAttribute('aria-hidden', 'true');
  }

  function badge(cls, text) { return `<span class="os-badge ${cls}">${text}</span>`; }

  function updateStockDisplay(quantity, unavailableInShopify) {
    const indicator = document.getElementById('os-stock-indicator');
    const bisForm = document.getElementById('os-bis-form');
    if (!indicator || !bisForm) return;

    hideBIS(bisForm);

    if (unavailableInShopify) {
      // Stand-out state: the tag beside the price replaces the badge.
      indicator.innerHTML = '';
      setSoldOutUI(true);
      showBIS(bisForm);
      return;
    }
    setSoldOutUI(false);

    if (quantity === null || quantity === undefined) {
      // No figure from the Worker and Shopify says sellable → nothing to show.
      indicator.innerHTML = '';
      return;
    }

    const showQty = config.showQuantity !== false;

    if (quantity <= 0) {
      indicator.innerHTML = badge('os-out-of-stock', str('outOfStock', 'Out of stock'));
      showBIS(bisForm);
    } else if (quantity <= 5) {
      indicator.innerHTML = badge('os-critical-stock', showQty ? str('onlyLeft', 'Only __COUNT__ left', { count: quantity }) : str('lowStock', 'Low stock'));
    } else if (quantity <= 10) {
      indicator.innerHTML = badge('os-low-stock', showQty ? str('onlyLeft', 'Only __COUNT__ left', { count: quantity }) : str('lowStock', 'Low stock'));
    } else {
      indicator.innerHTML = badge('os-in-stock', showQty ? str('inStockCount', 'In stock — __COUNT__ available', { count: quantity }) : str('inStock', 'In stock'));
    }
  }

  // ─── Inventory Refresh ─────────────────────────────────────────────────────

  let currentVariantId = config.currentVariantId;

  async function refreshInventory(variantId) {
    // Shopify's verdict is the fallback when the Worker has no figure for this variant.
    const soldOut = isUnavailableInShopify(variantId);

    const market = getMarketFromUrl();
    if (!market) {
      // No market → submitBIS would fail anyway, so don't offer the form.
      updateStockDisplay(null, false);
      return;
    }

    // Prefetched batch hit → instant, no network call.
    const key = String(variantId);
    if (inventoryByVariant && Object.prototype.hasOwnProperty.call(inventoryByVariant, key)) {
      updateStockDisplay(inventoryByVariant[key], soldOut);
      return;
    }

    const indicator = document.getElementById('os-stock-indicator');
    if (indicator) {
      indicator.innerHTML = badge('os-loading', str('checking', 'Checking stock…'));
    }

    const quantity = await getInventoryForMarket(variantId, market);
    updateStockDisplay(quantity, soldOut);
    // Remember it so a later switch back to this variant is instant.
    if (inventoryByVariant) inventoryByVariant[key] = quantity;
  }

  // ─── Variant Change Listeners ──────────────────────────────────────────────

  function setupVariantListeners() {
    // Native variant:changed event (some themes dispatch this)
    document.addEventListener('variant:changed', (event) => {
      const variant = event.detail?.variant;
      if (variant && variant.id !== currentVariantId) {
        currentVariantId = variant.id;
        refreshInventory(currentVariantId);
      }
    });

    // Resolve option-based selections against the product's variants (parsed in init).
    const variants = allVariants;

    // Recommendation and quick-add blocks reuse the .productOption class, and a
    // checked swatch in one of those would be pushed into `selected`, breaking the
    // length match so the variant silently stopped updating. Scope to this product.
    const host = document.getElementById('oceansouth-inventory');
    const root = (host && host.closest('.yv_product_content_section')) || document;

    function resolveVariantFromOptions() {
      const selected = [];
      root.querySelectorAll('.productOption').forEach(el => {
        if (el.tagName === 'SELECT' && el.name && el.name.startsWith('option')) {
          selected.push(el.value);
        } else if (el.type === 'radio' && el.checked) {
          selected.push(el.value);
        }
      });
      if (!selected.length) return null;
      const match = variants.find(v =>
        Array.isArray(v.options) &&
        v.options.length === selected.length &&
        v.options.every((opt, i) => opt === selected[i])
      );
      return match ? match.id : null;
    }

    function onOptionChange() {
      const newVariantId = resolveVariantFromOptions();
      if (newVariantId && newVariantId !== currentVariantId) {
        currentVariantId = newVariantId;
        refreshInventory(currentVariantId);
      }
    }

    root.querySelectorAll('.productOption').forEach(el => {
      el.addEventListener('change', onOptionChange);
    });

    // URL change detection (theme updates ?variant= query string)
    let lastUrl = window.location.href;
    const observer = new MutationObserver(() => {
      const currentUrl = window.location.href;
      if (currentUrl !== lastUrl) {
        lastUrl = currentUrl;
        const variantParam = new URLSearchParams(window.location.search).get('variant');
        if (variantParam) {
          const parsed = parseInt(variantParam, 10);
          if (parsed && parsed !== currentVariantId) {
            currentVariantId = parsed;
            refreshInventory(currentVariantId);
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Legacy hidden input pattern
    document.querySelectorAll('select[name="id"], input[name="id"]').forEach(el => {
      el.addEventListener('change', (e) => {
        const newVariantId = parseInt(e.target.value, 10);
        if (newVariantId && newVariantId !== currentVariantId) {
          currentVariantId = newVariantId;
          refreshInventory(currentVariantId);
        }
      });
    });
  }

  // ─── Back In Stock Subscription ────────────────────────────────────────────

  async function submitBIS(email, variantId) {
    if (!config.klaviyoPublicKey) {
      console.error('Oceansouth: No Klaviyo public key configured');
      return false;
    }

    const market = getMarketFromUrl();
    if (!market) {
      console.error('Oceansouth: Unable to determine market from URL');
      return false;
    }

    const klaviyoVariantId = `$shopify:::$default:::${variantId}`;

    // Step 1 — Native Klaviyo BIS subscription. NON-FATAL: the Worker (Step 4) is
    // the source of truth for restock notifications and the native flow is retired
    // (Draft). We still create the native subscription for now, but a failure here
    // must NOT abort the signup — otherwise the Worker registration below never runs.
    try {
      const bisResponse = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${config.klaviyoPublicKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': '2024-02-15'
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                channels: ['EMAIL'],
                profile: {
                  data: {
                    type: 'profile',
                    attributes: { email }
                  }
                }
              },
              relationships: {
                variant: {
                  data: {
                    type: 'catalog-variant',
                    id: klaviyoVariantId
                  }
                }
              }
            }
          })
        }
      );
      if (!bisResponse.ok && bisResponse.status !== 202) {
        console.warn('Oceansouth: native BIS subscription failed (non-fatal)', bisResponse.status);
      }
    } catch (e) {
      console.warn('Oceansouth: native BIS subscription error (non-fatal)', e);
    }

    // Step 2 — Update profile with Market property
    // This is critical — the Worker uses this to filter notifications
    const profileResponse = await fetch(
      `https://a.klaviyo.com/client/profiles/?company_id=${config.klaviyoPublicKey}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2024-02-15'
        },
        body: JSON.stringify({
          data: {
            type: 'profile',
            attributes: {
              email,
              properties: {
                Market: market
              }
            }
          }
        })
      }
    );

    if (!profileResponse.ok && profileResponse.status !== 200 && profileResponse.status !== 201) {
      console.warn('Oceansouth: Profile update warning', profileResponse.status);
      // Don't fail — BIS subscription was successful
    }

    // Step 3 — Set email marketing consent on the per-market BIS list.
    // Required because the restock email is sent by a custom-metric flow
    // ("Back In Stock - {MARKET}") with a *marketing* (non-transactional) email.
    // Without marketing consent that flow skips the profile ("not subscribed to
    // email"), so the BIS subscription + Market property alone are not enough.
    // Opt-in behaviour is configured per-list in Klaviyo: AU/US/NZ are single
    // opt-in (consent → SUBSCRIBED immediately); EU is double opt-in (consent →
    // PENDING until the subscriber confirms, then the flow sends). The request
    // body is identical for every market — only the list settings differ.
    const bisListId = (config.klaviyoBisListIds || {})[market];
    if (bisListId) {
      const consentResponse = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${config.klaviyoPublicKey}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // The marketing-consent body shape (profile.attributes.subscriptions)
            // is only valid from Klaviyo API revision 2025-01-15+. Older revisions
            // (e.g. 2024-02-15 used by the other calls) reject it with HTTP 400.
            'revision': '2025-07-15'
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Back in Stock form',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      subscriptions: {
                        email: { marketing: { consent: 'SUBSCRIBED' } }
                      }
                    }
                  }
                }
              },
              relationships: {
                list: { data: { type: 'list', id: bisListId } }
              }
            }
          })
        }
      );

      if (!consentResponse.ok && consentResponse.status !== 202) {
        console.warn('Oceansouth: marketing consent warning', consentResponse.status);
        // Don't fail — BIS subscription itself succeeded; consent can be retried.
      }
    } else {
      console.warn('Oceansouth: no BIS list configured for market', market);
    }

    // Step 4 — Register with the Oceansouth Worker. The Worker keeps its own
    // per-variant/market subscriber store and fires the market-scoped
    // "Back In Stock - {MARKET}" event on restock. This is what actually drives
    // the notification: Klaviyo's BIS API is create-only (it can't be queried
    // for who subscribed), so Steps 1-3 only set up the Klaviyo profile/consent.
    if (config.workerUrl) {
      try {
        const workerResponse = await fetch(`${config.workerUrl}/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ variant_id: variantId, market, email })
        });
        if (!workerResponse.ok) {
          console.warn('Oceansouth: worker subscribe warning', workerResponse.status);
        }
      } catch (e) {
        console.warn('Oceansouth: worker subscribe error', e);
      }
    } else {
      console.warn('Oceansouth: no worker URL configured for BIS subscribe');
    }

    return true;
  }

  // ─── BIS Form UI ───────────────────────────────────────────────────────────

  function maskEmail(email) {
    const at = email.indexOf('@');
    if (at < 1) return email;
    return email.charAt(0) + '•••' + email.slice(at);
  }

  function showSuccess(email) {
    const panel = document.getElementById('os-bis-panel');
    const success = document.getElementById('os-bis-success');
    const text = document.getElementById('os-bis-success-text');
    if (!panel || !success) return;
    if (text) {
      text.textContent = '';
      const parts = str('successText', "We'll email __EMAIL__ the moment it's back in stock.").split('__EMAIL__');
      text.appendChild(document.createTextNode(parts[0]));
      const strong = document.createElement('strong');
      strong.textContent = maskEmail(email);
      text.appendChild(strong);
      text.appendChild(document.createTextNode(parts.slice(1).join('')));
    }
    panel.hidden = true;
    success.hidden = false;
    subscribed = true;
    updateBar();
  }

  function showPanelAgain() {
    const panel = document.getElementById('os-bis-panel');
    const success = document.getElementById('os-bis-success');
    const emailInput = document.getElementById('os-bis-email');
    const submitBtn = document.getElementById('os-bis-submit');
    if (!panel || !success) return;
    success.hidden = true;
    panel.hidden = false;
    subscribed = false;
    if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = str('button', "Notify me when it's back"); }
    if (emailInput) emailInput.focus();
    updateBar();
  }

  function setupBISForm() {
    const submitBtn = document.getElementById('os-bis-submit');
    const emailInput = document.getElementById('os-bis-email');
    const message = document.getElementById('os-bis-message');
    const changeBtn = document.getElementById('os-bis-change');

    if (!submitBtn || !emailInput || !message) return;

    submitBtn.addEventListener('click', async () => {
      const email = emailInput.value.trim();

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        showMessage(str('invalidEmail', 'Please enter a full email address'), 'error');
        emailInput.setAttribute('aria-invalid', 'true');
        emailInput.focus();
        return;
      }
      emailInput.removeAttribute('aria-invalid');
      hideMessage();

      submitBtn.disabled = true;
      submitBtn.textContent = str('submitting', 'Sending…');

      try {
        const success = await submitBIS(email, currentVariantId);
        if (success) {
          showSuccess(email);
        } else {
          showMessage(str('error', 'Something went wrong. Please try again.'), 'error');
          submitBtn.disabled = false;
          submitBtn.textContent = str('button', "Notify me when it's back");
        }
      } catch (e) {
        console.error('Oceansouth: BIS submit error', e);
        showMessage(str('error', 'Something went wrong. Please try again.'), 'error');
        submitBtn.disabled = false;
        submitBtn.textContent = str('button', "Notify me when it's back");
      }
    });

    emailInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') { e.preventDefault(); submitBtn.click(); }
    });
    emailInput.addEventListener('input', () => { emailInput.removeAttribute('aria-invalid'); hideMessage(); });
    if (changeBtn) changeBtn.addEventListener('click', showPanelAgain);
  }

  function showMessage(text, type) {
    const message = document.getElementById('os-bis-message');
    if (!message) return;
    message.textContent = text;
    message.className = `os-bis-message os-bis-message--${type}`;
    message.hidden = false;
  }

  function hideMessage() {
    const message = document.getElementById('os-bis-message');
    if (message) message.hidden = true;
  }

  // ─── Phone: sticky "Notify me" bar once the panel has scrolled out of view ──

  function setupStickyBar() {
    const bar = document.getElementById('os-bis-bar');
    const panel = document.getElementById('os-bis-form');
    const btn = document.getElementById('os-bis-bar-btn');
    if (!bar || !panel || !btn) return;
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        // a hidden panel reports "not intersecting" — soldOutUI gates the bar in that case
        barSuppressed = entries.some((en) => en.isIntersecting);
        updateBar();
      }, { threshold: 0.15 });
      io.observe(panel);
    }
    btn.addEventListener('click', () => {
      panel.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const input = document.getElementById('os-bis-email');
      if (input && !subscribed) setTimeout(() => input.focus({ preventScroll: true }), 450);
    });
  }

  async function init() {
    // Only run on product pages
    if (!document.getElementById('oceansouth-inventory')) return;

    allVariants = loadVariants();
    setupVariantListeners();
    setupBISForm();
    setupStickyBar();
    watchPriceBox();

    // Prefetch every variant's stock in one request, so variant switches read
    // from memory. Falls back to per-variant fetch if the batch is unavailable.
    const market = getMarketFromUrl();
    if (market) {
      const ids = allVariants.map(v => String(v.id)).filter(Boolean);
      const cur = String(currentVariantId || '');
      if (cur && !ids.includes(cur)) ids.push(cur);
      inventoryByVariant = await prefetchInventory(ids, market);
    }

    // Initial inventory load (reads from the prefetched map when available)
    refreshInventory(currentVariantId);
  }

  // Wait for DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
